import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";
import { EXAM_TOPICS, getExamTopic, type ExamTopic } from "@/lib/exam-topics";
import { listTopicQuestions } from "@/lib/questions.functions";

export const Route = createFileRoute("/provfragor/$topic")({
  loader: async ({ params }) => {
    const topic = getExamTopic(params.topic);
    if (!topic) throw notFound();
    let questions: { id: string; question: string; options: string[] }[] = [];
    try {
      questions = await listTopicQuestions({ data: { category: topic.category, limit: 8 } });
    } catch {
      questions = [];
    }
    return { topic, questions };
  },
  head: ({ params, loaderData }) => {
    const topic = loaderData?.topic ?? getExamTopic(params.topic);
    if (!topic) {
      return { meta: [{ title: "Provfrågor – Alkoholprovet" }, { name: "robots", content: "noindex" }] };
    }
    const url = `https://alkoholprovet.se/provfragor/${topic.slug}`;
    return {
      meta: [
        { title: topic.metaTitle },
        { name: "description", content: topic.metaDescription },
        { property: "og:title", content: topic.h1 },
        { property: "og:description", content: topic.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: topic.h1 },
        { name: "twitter:description", content: topic.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqJsonLd(topic.faqs),
        breadcrumbJsonLd([
          { label: "Hem", url: "https://alkoholprovet.se/" },
          { label: "Provfrågor", url: "https://alkoholprovet.se/provfragor" },
          { label: topic.category, url },
        ]),
      ],
    };
  },
  component: TopicPage,
});

function TopicPage() {
  const { topic, questions } = Route.useLoaderData() as {
    topic: ExamTopic;
    questions: { id: string; question: string; options: string[] }[];
  };

  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Provfrågor", to: "/provfragor" },
        { label: topic.category },
      ]}
      title={topic.h1}
      intro={topic.intro}
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING]}
      sections={topic.sections}
      faqs={topic.faqs}
      offerBlock={
        questions.length > 0 ? (
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-semibold">
              Exempel på övningsfrågor – {topic.category}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Frågorna nedan är hämtade ur vår frågebank. Rätt svar och förklaring med lagrumshänvisning
              visas när du svarar i övningsläget.
            </p>
            <ol className="mt-5 space-y-5">
              {questions.map((q, i) => (
                <li key={q.id} className="rounded-xl border border-border/70 bg-background p-4">
                  <p className="font-medium">
                    {i + 1}. {q.question}
                  </p>
                  <ul className="mt-2 grid gap-1 text-sm text-muted-foreground">
                    {q.options.map((o, oi) => (
                      <li key={oi}>{String.fromCharCode(65 + oi)}. {o}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
            <Link
              to="/ovningsprov-serveringstillstand"
              className="mt-6 inline-flex rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Öva med svar och förklaringar
            </Link>
          </section>
        ) : undefined
      }
      related={{
        title: "Fler ämnesområden",
        links: [
          ...EXAM_TOPICS.filter((t) => t.slug !== topic.slug).map((t) => ({
            to: `/provfragor/${t.slug}`,
            label: `Provfrågor ${t.category.toLowerCase()}`,
          })),
          { to: "/provfragor", label: "Alla ämnesområden" },
          { to: "/gratis-ovningsfragor-serveringstillstand", label: "Gratis övningsfrågor" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
        ],
      }}
    />
  );
}
